import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { ToastService } from 'src/app/services/toast.service';
import { ssoConnectionsService } from 'src/app/shared/vive-api/sso-connections.service';
import {
  SsoConnectionDto,
  SsoConnectionsRoleMappingRequestDto
} from 'src/app/shared/vive-api/vive-api-models';

import {
  SSO_ROLES_FOR_SCOPE,
  SSO_ROLE_BY_ID,
  SSO_ROLE_LABELS,
  SSO_SAVE_FAILED,
  SsoRole,
  ssoApiErrorMessage
} from '../../sso-connections';

interface MappingRow {
  idpGroup: string;
  role: SsoRole | null;
  priority: number | null;
}

// The API's limit on one save.
const MAX_MAPPINGS = 50;
const PRIORITY_STEP = 10;

const rowsOf = (connection: SsoConnectionDto): MappingRow[] =>
  (connection.RoleMappings ?? []).map(
    ({ idpGroup, UserAccountRoleId, priority }) => ({
      idpGroup,
      role: SSO_ROLE_BY_ID.get(UserAccountRoleId) ?? null,
      priority
    })
  );

const comparable = (rows: readonly MappingRow[]): string =>
  JSON.stringify(
    rows.map(({ idpGroup, role, priority }) => [
      idpGroup.trim(),
      role,
      priority
    ])
  );

@Component({
  selector: 'app-sso-connection-role-mappings-card',
  templateUrl: './sso-connection-role-mappings-card.component.html',
  styleUrl: './sso-connection-role-mappings-card.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SsoConnectionRoleMappingsCardComponent implements OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly toastService = inject(ToastService);

  @Input({ required: true }) public connection!: SsoConnectionDto;
  @Output() public readonly saved = new EventEmitter<SsoConnectionDto>();

  public readonly maxMappings = MAX_MAPPINGS;
  public readonly roleLabels = SSO_ROLE_LABELS;
  public roleOptions: readonly SsoRole[] = [];
  public rows: MappingRow[] = [];
  // A mapping saved by another client to a role missing from SSO_ROLE_BY_ID.
  // Editing would drop it on save, so the card turns read-only instead.
  public hasUnknownRole = false;
  public saving = false;
  public error: string | null = null;

  public get canSave(): boolean {
    return (
      !this.saving &&
      !this.hasUnknownRole &&
      this.rows.every(
        ({ idpGroup, role, priority }) =>
          idpGroup.trim() !== '' &&
          role !== null &&
          priority !== null &&
          Number.isInteger(priority) &&
          priority >= 0
      ) &&
      comparable(this.rows) !== comparable(rowsOf(this.connection))
    );
  }

  // Staff groups mapped to ADMIN_ADMIN, which reaches this screen too.
  public get fullAdminWarnings(): string[] {
    return this.connection.scope === 'staff'
      ? this.rows
          .filter(
            ({ idpGroup, role }) =>
              role === 'ADMIN_ADMIN' && idpGroup.trim() !== ''
          )
          .map(
            ({ idpGroup }) =>
              `Everyone in ${idpGroup.trim()} becomes a full VIVE admin, including access to this screen.`
          )
      : [];
  }

  // Seeds the rows once, for the same reason as the domains card: later inputs
  // come from elsewhere on the page and must not wipe unsaved rows.
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['connection']?.firstChange) {
      this.roleOptions = SSO_ROLES_FOR_SCOPE[this.connection.scope];
      this.reset(this.connection);
    }
  }

  public addRow(): void {
    if (this.rows.length >= MAX_MAPPINGS) {
      return;
    }

    const lowest = Math.max(...this.rows.map(({ priority }) => priority ?? 0));
    this.rows = [
      ...this.rows,
      {
        idpGroup: '',
        role: null,
        priority: this.rows.length === 0 ? 0 : lowest + PRIORITY_STEP
      }
    ];
  }

  public removeRow(index: number): void {
    this.rows = this.rows.filter((_, i) => i !== index);
  }

  public async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    // canSave has checked every row; this only narrows the types.
    const mappings: SsoConnectionsRoleMappingRequestDto[] = this.rows.flatMap(
      ({ idpGroup, role, priority }) =>
        role === null || priority === null
          ? []
          : [{ idpGroup: idpGroup.trim(), userAccountRole: role, priority }]
    );

    this.saving = true;
    this.error = null;
    this.cdr.markForCheck();

    try {
      const updated = await ssoConnectionsService.replaceRoleMappings({
        pathParams: { connectionId: this.connection.id },
        data: { mappings }
      });
      this.reset(updated);
      this.saved.emit(updated);
      this.toastService.addSuccessToast('Saved', 'Your changes are saved.');
    } catch (error) {
      this.error = ssoApiErrorMessage(error, SSO_SAVE_FAILED);
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  private reset(connection: SsoConnectionDto): void {
    this.connection = connection;
    this.rows = rowsOf(connection);
    this.hasUnknownRole = this.rows.some(({ role }) => role === null);
  }
}
